import { useEffect, useRef, useState } from 'react';

interface Seg {
  count: number;
  color: string;
}

const VIEW = 100;
const R = 44;
const SW = 9;
const CIRC = 100; // pathLength-normalised
const OVERLAP = 0.6; // segments overlap a hair so seams close (no track hairline)
const TRACK = '#F0E8DC';
const TOTAL_MS = 950;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * SVG gauge ring: each segment's arc sweeps in from length 0 to its value
 * (clockwise from the top), like a speedometer filling up. The centre number
 * counts up in sync. Respects prefers-reduced-motion.
 */
export function RingCircle({
  segments,
  total,
  center,
  centerSub,
  size = 128,
  centerColor = '#111111',
  animationDelay = 0,
}: {
  segments: readonly Seg[];
  total?: number;
  center: string;
  centerSub: string;
  size?: number;
  centerColor?: string;
  animationDelay?: number;
}) {
  const t = (total ?? segments.reduce((s, p) => s + p.count, 0)) || 1;
  const on = segments.filter((p) => p.count > 0);

  // Lay out each arc flush against the previous one, with a hair of overlap so
  // adjacent segments' seams close cleanly (later arcs paint over the overlap).
  const multi = on.length > 1;
  let cum = 0;
  const arcs = on.map((s) => {
    const raw = (s.count / t) * CIRC;
    const len = multi ? raw + OVERLAP : raw;
    const a = { color: s.color, len, offset: cum, delayFrac: cum / CIRC, durFrac: raw / CIRC };
    cum += raw;
    return a;
  });

  const arcRefs = useRef<(SVGCircleElement | null)[]>([]);
  const arcSig = arcs.map((a) => `${a.len.toFixed(2)}:${a.offset.toFixed(2)}`).join('|');
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const anims = arcs.map((a, i) => {
      const el = arcRefs.current[i];
      if (!el) return null;
      return el.animate(
        [{ strokeDasharray: `0 ${CIRC}` }, { strokeDasharray: `${a.len} ${CIRC - a.len}` }],
        {
          duration: Math.max(160, a.durFrac * TOTAL_MS),
          delay: animationDelay * 1000 + a.delayFrac * TOTAL_MS,
          easing: 'cubic-bezier(.22,.61,.36,1)',
          fill: 'both',
        },
      );
    });
    return () => anims.forEach((an) => an?.cancel());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arcSig, animationDelay]);

  // ---- Centre count-up ----
  const m = center.match(/^(\d+)(.*)$/);
  const targetNum = m ? Number(m[1]) : null;
  const suffix = m ? m[2] : '';
  const [display, setDisplay] = useState(() =>
    targetNum == null || prefersReducedMotion() ? center : `0${suffix}`,
  );
  useEffect(() => {
    if (targetNum == null || prefersReducedMotion()) {
      setDisplay(center);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const delay = animationDelay * 1000;
    const tick = (nowT: number) => {
      const p = Math.min(1, Math.max(0, (nowT - start - delay) / TOTAL_MS));
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(`${Math.round(eased * targetNum)}${suffix}`);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [center, targetNum, suffix, animationDelay]);

  return (
    <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg width={size} height={size} viewBox={`0 0 ${VIEW} ${VIEW}`} style={{ display: 'block' }}>
        <g transform={`rotate(-90 ${VIEW / 2} ${VIEW / 2})`}>
          <circle
            cx={VIEW / 2}
            cy={VIEW / 2}
            r={R}
            fill="none"
            stroke={TRACK}
            strokeWidth={SW}
            pathLength={CIRC}
          />
          {arcs.map((a, i) => (
            <circle
              key={i}
              ref={(el) => {
                arcRefs.current[i] = el;
              }}
              cx={VIEW / 2}
              cy={VIEW / 2}
              r={R}
              fill="none"
              stroke={a.color}
              strokeWidth={SW}
              strokeLinecap="butt"
              pathLength={CIRC}
              strokeDasharray={`${a.len} ${CIRC - a.len}`}
              strokeDashoffset={-a.offset}
            />
          ))}
        </g>
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <span
          style={{
            fontSize: size * 0.27,
            fontWeight: 900,
            color: centerColor,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {display}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#888888',
            marginTop: 4,
          }}
        >
          {centerSub}
        </span>
      </div>
    </div>
  );
}
